// =============================================================================
// Origin UI — src/pages/admin/AdminConfigPage.tsx
// Admin configuration console with tabs:
//   1. Task Catalog — manage task types
//   2. Mapping Rules — configure which selections generate which tasks
//   3. Departments — manage department reference data
//   4. Office Locations — manage office location reference data
//   5. IT Staff — manage IT staff members
//   6. Dept Templates — default selections per department
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';
import { LeaveAdminTab } from './LeaveAdminTab';
import { EvaluationAdminTab } from './EvaluationAdminTab';
import { OfficeDetailAdminTab } from './OfficeDetailAdminTab';
import { ExchangeRateAdminTab } from './ExchangeRateAdminTab';
import { LetterTemplateAdminTab } from './LetterTemplateAdminTab';
import { OnboardingChecklistAdminTab } from './OnboardingChecklistAdminTab';
import { OnboardingTaskTemplateAdminTab } from './OnboardingTaskTemplateAdminTab';
import { CostCentreAdminTab } from './CostCentreAdminTab';
import { HelpKnowledgeBaseAdminTab } from './HelpKnowledgeBaseAdminTab';
import { HelpFeedbackAdminTab } from './HelpFeedbackAdminTab';
import ModuleSettingsAdminTab from './ModuleSettingsAdminTab';

// ─── Types ───

interface TaskCatalog {
  taskCatalogId: number; taskType: string; description: string;
  defaultAssignmentGroup: string | null; sortOrder: number; isActive: boolean;
}
interface MappingRule {
  taskMappingRuleId: number; selectionType: string; selectionValue: string;
  requestType: string | null; taskCatalogId: number; taskType: string;
  priority: number; isActive: boolean;
}

type Tab = 'catalog' | 'rules' | 'departments' | 'locations' | 'itstaff' | 'templates' | 'roles' | 'leave' | 'evaluations' | 'oqtemplate' | 'officedetails' | 'exchangerates' | 'lettertemplates' | 'checklists' | 'tasktemplates' | 'costcentres' | 'entralink' | 'helpkb' | 'helpfeedback' | 'visadoctypes' | 'modules';

type Category = 'onboarding' | 'people' | 'leave' | 'performance' | 'letters' | 'aiassistant' | 'system';

const categories: { key: Category; label: string; icon: string; tabs: { key: Tab; label: string }[]; displayCount?: number; allowedRoles: ('HRAdmin' | 'SysAdmin' | 'ITOps' | 'CreditCardAdmin')[] }[] = [
  {
    key: 'onboarding', label: 'Onboarding', icon: '📋',
    allowedRoles: ['SysAdmin', 'HRAdmin', 'ITOps'],
    tabs: [
      { key: 'catalog', label: 'Task Catalog' },
      { key: 'rules', label: 'Mapping Rules' },
      { key: 'templates', label: 'Dept Templates' },
      { key: 'checklists', label: 'Checklists' },
      { key: 'tasktemplates', label: 'Task Templates' },
    ]
  },
  {
    key: 'people', label: 'People', icon: '👥',
    allowedRoles: ['SysAdmin', 'HRAdmin'],
    tabs: [
      { key: 'departments', label: 'Departments' },
      { key: 'locations', label: 'Office Locations' },
      { key: 'roles', label: 'Roles' },
      { key: 'oqtemplate', label: 'OQ Template' },
      { key: 'visadoctypes', label: 'Document Types' },
    ]
  },
  {
    key: 'leave', label: 'Leave', icon: '🏖️',
    allowedRoles: ['HRAdmin', 'SysAdmin'],
    tabs: [
      { key: 'leave', label: 'Leave Settings' },
    ],
    displayCount: 5
  },
  {
    key: 'performance', label: 'Performance', icon: '📝',
    allowedRoles: ['HRAdmin', 'SysAdmin'],
    tabs: [
      { key: 'evaluations', label: 'Evaluation Settings' },
    ],
    displayCount: 4
  },
  {
    key: 'letters', label: 'Letters', icon: '✉️',
    allowedRoles: ['HRAdmin', 'SysAdmin'],
    tabs: [
      { key: 'lettertemplates', label: 'Templates' },
      { key: 'officedetails', label: 'Office Details' },
      { key: 'exchangerates', label: 'Exchange Rates' },
    ]
  },
  {
    key: 'aiassistant', label: 'AI Assistant', icon: '🤖',
    allowedRoles: ['HRAdmin', 'SysAdmin'],
    tabs: [
      { key: 'helpkb', label: 'Knowledge Base' },
      { key: 'helpfeedback', label: 'Feedback' },
    ]
  },
  {
    key: 'system', label: 'System', icon: '⚙️',
    allowedRoles: ['SysAdmin', 'HRAdmin'],
    tabs: [
      { key: 'entralink', label: 'Entra ID Linking' },
      { key: 'modules', label: 'Modules' },
    ]
  },
];

export function AdminConfigPage() {
  const { roles } = useAuth();
  
  // Filter categories based on user role
  const visibleCategories = categories.filter(cat => {
    if (roles.isHRAdmin && cat.allowedRoles.includes('HRAdmin')) return true;
    if (roles.isSysAdmin && cat.allowedRoles.includes('SysAdmin')) return true;
    if (roles.isITOps && cat.allowedRoles.includes('ITOps')) return true;
    if (roles.isCreditCardAdmin && cat.allowedRoles.includes('CreditCardAdmin')) return true;
    return false;
  });

  const defaultCat = visibleCategories[0]?.key || 'onboarding';
  const defaultTab = visibleCategories[0]?.tabs[0]?.key || 'catalog';

  const [activeCategory, setActiveCategory] = useState<Category>(defaultCat);
  const [tab, setTab] = useState<Tab>(defaultTab);

  function selectCategory(cat: Category) {
    setActiveCategory(cat);
    const first = categories.find(c => c.key === cat)?.tabs[0];
    if (first) setTab(first.key);
  }

  const currentCategory = categories.find(c => c.key === activeCategory);

  if (visibleCategories.length === 0) {
    return (
      <div>
        <PageHeader title="Admin Configuration" subtitle="Manage task mappings, reference data, and system settings" />
        <Card><p style={{ padding: 20, textAlign: 'center', color: 'var(--gray-500)' }}>You don't have access to any configuration areas.</p></Card>
      </div>
    );
  }
  const currentTabs = currentCategory?.tabs || [];

  return (
    <div>
      <PageHeader title="Admin Configuration" subtitle="Manage task mappings, reference data, and system settings" />

      <div className={styles.adminLayout}>
        {/* Left category menu */}
        <div className={styles.categoryMenu}>
          {visibleCategories.map(cat => (
            <button
              key={cat.key}
              className={`${styles.categoryBtn} ${activeCategory === cat.key ? styles.categoryBtnActive : ''}`}
              onClick={() => selectCategory(cat.key)}
            >
              <span className={styles.categoryIcon}>{cat.icon}</span>
              <span className={styles.categoryLabel}>{cat.label}</span>
              <span className={styles.categoryCount}>{cat.displayCount ?? cat.tabs.length}</span>
            </button>
          ))}
        </div>

        {/* Right content area */}
        <div className={styles.adminContent}>
          {/* Sub-tabs for categories with multiple items */}
          {currentTabs.length > 1 && (
            <div className={styles.subTabs}>
              {currentTabs.map(t => (
                <button
                  key={t.key}
                  className={`${styles.subTab} ${tab === t.key ? styles.subTabActive : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {tab === 'catalog' && <TaskCatalogTab />}
          {tab === 'rules' && <MappingRulesTab />}
          {tab === 'departments' && <ReferenceTab endpoint="departments" idField="departmentId" label="Department" />}
          {tab === 'locations' && <ReferenceTab endpoint="office-locations" idField="officeLocationId" label="Office Location" />}
          {tab === 'itstaff' && <ITStaffTab />}
          {tab === 'templates' && <DeptTemplatesTab />}
          {tab === 'roles' && <RolesTab />}
          {tab === 'leave' && <LeaveAdminTab />}
          {tab === 'evaluations' && <EvaluationAdminTab />}
          {tab === 'oqtemplate' && <OqTemplateTab />}
          {tab === 'visadoctypes' && <ReferenceTab endpoint="visa-document-types" idField="visaDocumentTypeId" label="Document Type" />}
          {tab === 'lettertemplates' && <LetterTemplateAdminTab />}
          {tab === 'officedetails' && <OfficeDetailAdminTab />}
          {tab === 'exchangerates' && <ExchangeRateAdminTab />}
          {tab === 'checklists' && <OnboardingChecklistAdminTab />}
          {tab === 'tasktemplates' && <OnboardingTaskTemplateAdminTab />}
          {tab === 'costcentres' && <CostCentreAdminTab />}
          {tab === 'helpkb' && <HelpKnowledgeBaseAdminTab />}
          {tab === 'helpfeedback' && <HelpFeedbackAdminTab />}
          {tab === 'entralink' && <EntraLinkTab />}
          {tab === 'modules' && <ModuleSettingsAdminTab />}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Task Catalog Tab
// =============================================================================

function TaskCatalogTab() {
  const [items, setItems] = useState<TaskCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<TaskCatalog | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ taskType: '', description: '', defaultAssignmentGroup: '', sortOrder: 0, isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setItems(await api.get('/api/admin/task-catalog')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ taskType: '', description: '', defaultAssignmentGroup: '', sortOrder: items.length + 1, isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: TaskCatalog) {
    setForm({ taskType: item.taskType, description: item.description, defaultAssignmentGroup: item.defaultAssignmentGroup || '', sortOrder: item.sortOrder, isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      if (editing) {
        await api.put(`/api/admin/task-catalog/${editing.taskCatalogId}`, form);
      } else {
        await api.post('/api/admin/task-catalog', form);
      }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{items.length} task types</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Task Type</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Task Type' : 'New Task Type'}</h4>
          <div className={styles.formGrid}>
            <FormField label="Task Type *" value={form.taskType} onChange={v => setForm({ ...form, taskType: v })} />
            <FormField label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <FormField label="Default Assignment Group" value={form.defaultAssignmentGroup} onChange={v => setForm({ ...form, defaultAssignmentGroup: v })} />
            <FormField label="Sort Order" type="number" value={String(form.sortOrder)} onChange={v => setForm({ ...form, sortOrder: Number(v) })} />
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr>
            <th>Task Type</th><th>Description</th><th>Group</th><th>Order</th><th>Active</th><th></th>
          </tr></thead>
          <tbody>
            {items.map(item => (
              <tr key={item.taskCatalogId}>
                <td className={styles.bold}>{item.taskType}</td>
                <td className={styles.muted}>{item.description}</td>
                <td className={styles.muted}>{item.defaultAssignmentGroup || '—'}</td>
                <td>{item.sortOrder}</td>
                <td><span className={item.isActive ? styles.active : styles.inactive}>{item.isActive ? 'Yes' : 'No'}</span></td>
                <td><button className={styles.editBtn} onClick={() => startEdit(item)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Mapping Rules Tab
// =============================================================================

function MappingRulesTab() {
  const [rules, setRules] = useState<MappingRule[]>([]);
  const [catalog, setCatalog] = useState<TaskCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<MappingRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({ selectionType: '', selectionValue: '*', requestType: '', taskCatalogId: 0, priority: 0, isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [r, c] = await Promise.all([
        api.get<MappingRule[]>('/api/admin/mapping-rules'),
        api.get<TaskCatalog[]>('/api/admin/task-catalog'),
      ]);
      setRules(r); setCatalog(c);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ selectionType: '', selectionValue: '*', requestType: '', taskCatalogId: catalog[0]?.taskCatalogId || 0, priority: rules.length + 1, isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(r: MappingRule) {
    setForm({ selectionType: r.selectionType, selectionValue: r.selectionValue, requestType: r.requestType || '', taskCatalogId: r.taskCatalogId, priority: r.priority, isActive: r.isActive });
    setEditing(r); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      if (editing) {
        await api.put(`/api/admin/mapping-rules/${editing.taskMappingRuleId}`, form);
      } else {
        await api.post('/api/admin/mapping-rules', form);
      }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await api.delete(`/api/admin/mapping-rules/${deleteId}`); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); setDeleteId(null); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{rules.length} mapping rules</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Rule</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Mapping Rule' : 'New Mapping Rule'}</h4>
          <div className={styles.formGrid}>
            <FormField label="Selection Type *" value={form.selectionType} onChange={v => setForm({ ...form, selectionType: v })} placeholder='e.g., Laptop, Software, * (wildcard)' />
            <FormField label="Selection Value" value={form.selectionValue} onChange={v => setForm({ ...form, selectionValue: v })} placeholder='e.g., MacBook Pro, * (wildcard)' />
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Request Type</label>
              <select className={styles.fieldInput} value={form.requestType} onChange={e => setForm({ ...form, requestType: e.target.value })}>
                <option value="">Any (all types)</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Offboarding">Offboarding</option>
                <option value="Transfer">Transfer</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Task Type *</label>
              <select className={styles.fieldInput} value={form.taskCatalogId} onChange={e => setForm({ ...form, taskCatalogId: Number(e.target.value) })}>
                {catalog.map(c => <option key={c.taskCatalogId} value={c.taskCatalogId}>{c.taskType}</option>)}
              </select>
            </div>
            <FormField label="Priority" type="number" value={String(form.priority)} onChange={v => setForm({ ...form, priority: Number(v) })} />
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr>
            <th>Priority</th><th>Selection</th><th>Request Type</th><th>→ Task</th><th>Active</th><th></th>
          </tr></thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.taskMappingRuleId}>
                <td>{r.priority}</td>
                <td><span className={styles.mono}>{r.selectionType}/{r.selectionValue}</span></td>
                <td className={styles.muted}>{r.requestType || 'Any'}</td>
                <td className={styles.bold}>{r.taskType}</td>
                <td><span className={r.isActive ? styles.active : styles.inactive}>{r.isActive ? 'Yes' : 'No'}</span></td>
                <td>
                  <button className={styles.editBtn} onClick={() => startEdit(r)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={() => setDeleteId(r.taskMappingRuleId)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ConfirmDialog
        open={deleteId !== null}
        title="Delete Mapping Rule"
        message="Are you sure? This will remove the rule permanently."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

// =============================================================================
// Generic Reference Data Tab (Departments / Office Locations)
// =============================================================================

function ReferenceTab({
  endpoint,
  idField,
  label,
}: {
  endpoint: string;
  idField: string;
  label: string;
}) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', sortOrder: 0, isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setItems(await api.get(`/api/admin/${endpoint}`)); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ name: '', sortOrder: items.length + 1, isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: any) {
    setForm({ name: item.name, sortOrder: item.sortOrder, isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      if (editing) {
        await api.put(`/api/admin/${endpoint}/${editing[idField]}`, form);
      } else {
        await api.post(`/api/admin/${endpoint}`, form);
      }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{items.length} {label.toLowerCase()}s</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add {label}</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? `Edit ${label}` : `New ${label}`}</h4>
          <div className={styles.formGrid}>
            <FormField label="Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <FormField label="Sort Order" type="number" value={String(form.sortOrder)} onChange={v => setForm({ ...form, sortOrder: Number(v) })} />
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr><th>Name</th><th>Order</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((item: any) => (
              <tr key={item[idField]}>
                <td className={styles.bold}>{item.name}</td>
                <td>{item.sortOrder}</td>
                <td><span className={item.isActive ? styles.active : styles.inactive}>{item.isActive ? 'Yes' : 'No'}</span></td>
                <td><button className={styles.editBtn} onClick={() => startEdit(item)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Managers Tab
// =============================================================================

// =============================================================================
// IT Staff Tab
// =============================================================================

interface ITStaffItem {
  itStaffId: string; fullName: string; email: string; entraObjectId: string; isActive: boolean;
}

function ITStaffTab() {
  const [items, setItems] = useState<ITStaffItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<ITStaffItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', entraObjectId: '', isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setItems(await api.get('/api/admin/it-staff')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ fullName: '', email: '', entraObjectId: '', isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: ITStaffItem) {
    setForm({ fullName: item.fullName, email: item.email, entraObjectId: item.entraObjectId, isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      if (editing) {
        await api.put(`/api/admin/it-staff/${editing.itStaffId}`, form);
      } else {
        await api.post('/api/admin/it-staff', form);
      }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{items.length} IT staff members</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add IT Staff</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit IT Staff' : 'New IT Staff'}</h4>
          <div className={styles.formGrid}>
            <FormField label="Full Name *" value={form.fullName} onChange={v => setForm({ ...form, fullName: v })} placeholder="e.g., John Smith" />
            <FormField label="Email *" value={form.email} onChange={v => setForm({ ...form, email: v })} placeholder="e.g., john.smith@omantrading.com" />
            <FormField label="Entra Object ID *" value={form.entraObjectId} onChange={v => setForm({ ...form, entraObjectId: v })} placeholder="e.g., 3c39151a-4ad0-47c5-..." />
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr><th>Name</th><th>Email</th><th>Entra ID</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map(item => (
              <tr key={item.itStaffId}>
                <td className={styles.bold}>{item.fullName}</td>
                <td className={styles.muted}>{item.email}</td>
                <td className={styles.mono}>{item.entraObjectId.slice(0, 8)}...</td>
                <td><span className={item.isActive ? styles.active : styles.inactive}>{item.isActive ? 'Yes' : 'No'}</span></td>
                <td><button className={styles.editBtn} onClick={() => startEdit(item)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Department Templates Tab
// =============================================================================

interface DeptTemplateItem {
  departmentTemplateId: number;
  departmentName: string;
  selectionType: string;
  selectionValue: string;
  additionalMetadata: string | null;
  isActive: boolean;
}

// Known selection types and their values for the dropdowns
const SELECTION_OPTIONS: Record<string, string[]> = {
  Hardware: ['Desktop', 'Camera', 'Headset', 'Laptop', 'iPhone', 'iPad'],
  Citrix: ['Citrix Access'],
  Software: ['LSEG', 'Vista', 'Diva', 'X2', 'LiveMTM', 'Vega', 'ICE Web', 'ICE IM'],
  FolderAccess: ['Trading Shared', 'Finance Shared', 'HR Shared', 'Operations Shared', 'Technology Shared', 'Risk Management Shared', 'Admin Shared', 'Analytics Shared'],
};

// These types use free text input instead of a dropdown for the value
const FREE_TEXT_TYPES = ['AppBatchDistribution', 'EmailDistributionLists', 'OtherAccess'];

const ALL_SELECTION_TYPES = [...Object.keys(SELECTION_OPTIONS), ...FREE_TEXT_TYPES];

function DeptTemplatesTab() {
  const [items, setItems] = useState<DeptTemplateItem[]>([]);
  const [departments, setDepartments] = useState<{ departmentId: number; name: string; isActive: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<DeptTemplateItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [filterDept, setFilterDept] = useState('');
  const [form, setForm] = useState({ departmentName: '', selectionType: 'Hardware', selectionValue: '', additionalMetadata: '', isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [t, d] = await Promise.all([
        api.get<DeptTemplateItem[]>('/api/admin/department-templates'),
        api.get<{ departmentId: number; name: string; isActive: boolean }[]>('/api/admin/departments'),
      ]);
      setItems(t);
      setDepartments(d);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    const defaultDept = filterDept || departments[0]?.name || '';
    setForm({ departmentName: defaultDept, selectionType: 'Hardware', selectionValue: SELECTION_OPTIONS['Hardware'][0] || '', additionalMetadata: '', isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: DeptTemplateItem) {
    setForm({ departmentName: item.departmentName, selectionType: item.selectionType, selectionValue: item.selectionValue, additionalMetadata: item.additionalMetadata || '', isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      if (editing) {
        await api.put(`/api/admin/department-templates/${editing.departmentTemplateId}`, form);
      } else {
        await api.post('/api/admin/department-templates', form);
      }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await api.delete(`/api/admin/department-templates/${deleteId}`); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); setDeleteId(null); }
  }

  // When selection type changes, reset selection value to first available option
  function handleTypeChange(newType: string) {
    const values = SELECTION_OPTIONS[newType] || [];
    const newValue = FREE_TEXT_TYPES.includes(newType) ? '' : (values[0] || '');
    setForm({ ...form, selectionType: newType, selectionValue: newValue, additionalMetadata: '' });
  }

  if (loading) return <LoadingSpinner />;

  // Filter and group
  const filtered = filterDept ? items.filter(i => i.departmentName === filterDept) : items;

  // Group by department for display
  const grouped: Record<string, DeptTemplateItem[]> = {};
  filtered.forEach(item => {
    if (!grouped[item.departmentName]) grouped[item.departmentName] = [];
    grouped[item.departmentName].push(item);
  });

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={styles.count}>{filtered.length} template items</span>
          <select
            className={styles.fieldInput}
            value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            style={{ fontSize: 13, padding: '4px 28px 4px 8px' }}
          >
            <option value="">All departments</option>
            {departments.filter(d => d.isActive).map(d => (
              <option key={d.departmentId} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Template Item</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Template Item' : 'New Template Item'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Department *</label>
              <select className={styles.fieldInput} value={form.departmentName} onChange={e => setForm({ ...form, departmentName: e.target.value })}>
                <option value="">Select department...</option>
                {departments.filter(d => d.isActive).map(d => (
                  <option key={d.departmentId} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Selection Type *</label>
              <select className={styles.fieldInput} value={form.selectionType} onChange={e => handleTypeChange(e.target.value)}>
                {ALL_SELECTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Selection Value *</label>
              {FREE_TEXT_TYPES.includes(form.selectionType) ? (
                <input className={styles.fieldInput} value={form.selectionValue} onChange={e => setForm({ ...form, selectionValue: e.target.value })} placeholder="Enter default text value..." />
              ) : (
                <select className={styles.fieldInput} value={form.selectionValue} onChange={e => setForm({ ...form, selectionValue: e.target.value })}>
                  {(SELECTION_OPTIONS[form.selectionType] || []).map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              )}
            </div>
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          {form.selectionType === 'FolderAccess' && (
            <div style={{ marginTop: 8 }}>
              <FormField label="Folder Path / Permissions" value={form.additionalMetadata} onChange={v => setForm({ ...form, additionalMetadata: v })} placeholder="e.g., \\server\Trading\Reports — Read/Write" />
            </div>
          )}
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      {Object.keys(grouped).length === 0 && (
        <Card>
          <p className={styles.muted} style={{ textAlign: 'center', padding: 20 }}>
            No department templates configured yet. Click "+ Add Template Item" to set default selections for a department.
          </p>
        </Card>
      )}

      {Object.entries(grouped).map(([deptName, deptItems]) => (
        <div key={deptName} style={{ marginBottom: 16 }}>
        <Card padding={false}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
            <strong style={{ color: 'var(--navy)', fontSize: 14 }}>{deptName}</strong>
            <span className={styles.muted} style={{ marginLeft: 8 }}>({deptItems.length} items)</span>
          </div>
          <table className={styles.table}>
            <thead><tr><th>Type</th><th>Value</th><th>Details</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {deptItems.map(item => (
                <tr key={item.departmentTemplateId}>
                  <td><span className={styles.mono}>{item.selectionType}</span></td>
                  <td className={styles.bold}>{item.selectionValue}</td>
                  <td className={styles.muted}>{item.additionalMetadata || '—'}</td>
                  <td><span className={item.isActive ? styles.active : styles.inactive}>{item.isActive ? 'Yes' : 'No'}</span></td>
                  <td>
                    <button className={styles.editBtn} onClick={() => startEdit(item)}>Edit</button>
                    <button className={styles.deleteBtn} onClick={() => setDeleteId(item.departmentTemplateId)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        </div>
      ))}

      <ConfirmDialog
        open={deleteId !== null}
        title="Delete Template Item"
        message="Are you sure? This will remove the template item permanently."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

// =============================================================================
// Roles Management Tab (Phase 1)
// =============================================================================

interface UserRole {
  userId: string;
  displayName: string;
  email: string;
  roles: {
    userRoleAssignmentId: string;
    role: string;
    assignedByDisplayName: string;
    assignedAt: string;
  }[];
}

const VALID_ROLES = ['HRAdmin', 'ITOps', 'SysAdmin', 'ComplianceExec', 'Compliance', 'OQTMan', 'CreditCardAdmin'] as const;

const ROLE_DESCRIPTIONS: Record<string, string> = {
  HRAdmin: 'Full people data access, reporting, can create requests',
  ITOps: 'IT task queue management, can assign and complete tasks',
  SysAdmin: 'Admin config access (no people data)',
  ComplianceExec: 'Compliance dashboard, admin, declaration viewing — for executive management',
  Compliance: 'Compliance team contact list only — no dashboard or admin access',
  OQTMan: 'Read-only view of all salary, banking and ID data — for executives. Cannot edit or export.',
};

const ROLE_COLOURS: Record<string, string> = {
  HRAdmin: '#00B0B9',
  ITOps: '#8B5CF6',
  SysAdmin: '#0A2240',
  ComplianceExec: '#16A34A',
  Compliance: '#65A30D',
  OQTMan: '#B45309',
};

function RolesTab() {
  const [users, setUsers] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokeLabel, setRevokeLabel] = useState('');
  const [form, setForm] = useState({ employeeId: '', role: 'HRAdmin' });
  const [employees, setEmployees] = useState<{ employeeId: string; employeeNumber: string; firstName: string; lastName: string; preferredFirstName: string | null; preferredLastName: string | null; }[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [r, emps] = await Promise.all([
        api.get<UserRole[]>('/api/admin/roles'),
        api.get<any[]>('/api/employees'),
      ]);
      setUsers(r);
      setEmployees(emps.map((e: any) => ({ employeeId: e.employeeId, employeeNumber: e.employeeNumber, firstName: e.firstName, lastName: e.lastName, preferredFirstName: e.preferredFirstName, preferredLastName: e.preferredLastName })));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ employeeId: '', role: 'HRAdmin' });
    setSearch('');
    setCreating(true);
  }

  function getEmpDisplayName(e: { firstName: string; lastName: string; preferredFirstName: string | null; preferredLastName: string | null }) {
    const first = e.preferredFirstName || e.firstName;
    const last = e.preferredLastName || e.lastName;
    return first + ' ' + last;
  }

  async function save() {
    try {
      setError('');
      if (!form.employeeId) { setError('Please select an employee.'); return; }
      await api.post('/api/admin/roles', { employeeId: form.employeeId, role: form.role });
      setCreating(false);
      await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  async function handleRevoke() {
    if (!revokeId) return;
    try {
      await api.delete(`/api/admin/roles/${revokeId}`);
      setRevokeId(null);
      setRevokeLabel('');
      await load();
    } catch (e: any) { setError(e.message); setRevokeId(null); }
  }

  if (loading) return <LoadingSpinner />;

  const totalRoles = users.reduce((sum, u) => sum + u.roles.length, 0);

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{users.length} users with {totalRoles} role assignments</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Assign Role</button>
      </div>

      {creating && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>Assign Role</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Employee</label>
              <input
                className={styles.fieldInput}
                type="text"
                placeholder="Search by name or employee number..."
                value={search}
                onChange={e => { setSearch(e.target.value); setForm({ ...form, employeeId: '' }); }}
              />
              {search.length >= 2 && !form.employeeId && (
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', marginTop: 4, background: 'white' }}>
                  {employees
                    .filter(e => {
                      const s = search.toLowerCase();
                      const name = getEmpDisplayName(e).toLowerCase();
                      return name.includes(s) || e.employeeNumber.toLowerCase().includes(s);
                    })
                    .slice(0, 10)
                    .map(e => (
                      <div
                        key={e.employeeId}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}
                        onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--gray-50)')}
                        onMouseLeave={ev => (ev.currentTarget.style.background = 'white')}
                        onClick={() => { setForm({ ...form, employeeId: e.employeeId }); setSearch(getEmpDisplayName(e) + ' (' + e.employeeNumber + ')'); }}
                      >
                        <span style={{ fontWeight: 600 }}>{getEmpDisplayName(e)}</span>
                        <span style={{ color: 'var(--gray-500)', marginLeft: 8 }}>{e.employeeNumber}</span>
                      </div>
                    ))
                  }
                  {employees.filter(e => { const s = search.toLowerCase(); return getEmpDisplayName(e).toLowerCase().includes(s) || e.employeeNumber.toLowerCase().includes(s); }).length === 0 && (
                    <div style={{ padding: '8px 12px', color: 'var(--gray-500)', fontSize: 13 }}>No employees found</div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Role</label>
              <select className={styles.fieldInput} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {VALID_ROLES.map(r => (
                  <option key={r} value={r}>{r} — {ROLE_DESCRIPTIONS[r]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => setCreating(false)}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={!form.employeeId}>Assign Role</button>
          </div>
        </Card>
      )}

      {users.length === 0 && !creating && (
        <Card>
          <p className={styles.muted} style={{ textAlign: 'center', padding: 20 }}>
            No role assignments found. Click "+ Assign Role" to grant a user HRAdmin, ITOps, SysAdmin, or CreditCardAdmin access.
          </p>
        </Card>
      )}

      {users.map(user => (
        <Card key={user.userId} className={styles.formCard} padding={true}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>{user.displayName || 'Unknown User'}</div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{user.email || user.userId}</div>
            </div>
            <div className={styles.mono} style={{ fontSize: 11 }}>{user.userId.slice(0, 8)}...</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {user.roles.map(role => (
              <div key={role.userRoleAssignmentId} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', borderRadius: 6,
                background: `${ROLE_COLOURS[role.role] || '#666'}15`,
                border: `1px solid ${ROLE_COLOURS[role.role] || '#666'}30`,
              }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: ROLE_COLOURS[role.role] || '#666' }}>
                  {role.role}
                </span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                  by {role.assignedByDisplayName}
                </span>
                <button
                  className={styles.deleteBtn}
                  style={{ padding: '2px 8px', fontSize: 11, marginRight: 0 }}
                  onClick={() => { setRevokeId(role.userRoleAssignmentId); setRevokeLabel(`${role.role} from ${user.displayName}`); }}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke Role"
        message={`Are you sure you want to revoke ${revokeLabel}? They will lose the associated access immediately.`}
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={handleRevoke}
        onCancel={() => { setRevokeId(null); setRevokeLabel(''); }}
      />
    </div>
  );
}

// =============================================================================
// OQ Email Template Tab (Task 4.11)
// =============================================================================

interface OqTemplateData {
  templateBody: string;
  updatedBy: string;
  updatedAt: string;
  availableTokens: string[];
}

function OqTemplateTab() {
  const [template, setTemplate] = useState<OqTemplateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const data = await api.get<OqTemplateData>('/api/admin/oq-template');
      setTemplate(data);
      setBody(data.templateBody);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function save() {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const data = await api.put<OqTemplateData>('/api/admin/oq-template', { templateBody: body });
      setTemplate(data);
      setSuccess('Template saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      const b = e.body ? JSON.parse(e.body) : null;
      setError(b?.error || e.message);
    } finally { setSaving(false); }
  }

  function insertToken(token: string) {
    setBody(prev => prev + token);
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
          {success}
        </div>
      )}

      <Card>
        <h4 className={styles.formTitle}>OQ Account Request Email Template</h4>
        <p className={styles.muted} style={{ marginBottom: 16 }}>
          This template is used when HR clicks "Request OQ Account" on an employee profile.
          Use the placeholder tokens below — they will be replaced with the employee's actual data.
        </p>

        <div style={{ marginBottom: 12 }}>
          <label className={styles.fieldLabel}>Available tokens (click to insert)</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {(template?.availableTokens || []).map(token => (
              <button
                key={token}
                onClick={() => insertToken(token)}
                style={{
                  padding: '4px 10px', borderRadius: 4, border: '1px solid var(--turquoise)',
                  background: 'var(--turquoise-light)', color: 'var(--turquoise-dark)',
                  fontSize: 12, fontWeight: 600, fontFamily: "'SF Mono', 'Menlo', monospace",
                  cursor: 'pointer',
                }}
              >
                {token}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Email body</label>
          <textarea
            className={styles.fieldInput}
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={18}
            style={{ fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical', minHeight: 300 }}
          />
        </div>

        <div className={styles.formActions}>
          <button className={styles.cancelBtn} onClick={() => setBody(template?.templateBody || '')}>Reset</button>
          <button className={styles.saveBtn} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>

        {template?.updatedBy && (
          <p className={styles.muted} style={{ marginTop: 12, fontSize: 12 }}>
            Last updated by {template.updatedBy} on {new Date(template.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// Entra ID Linking Tab — manual per-employee Entra Object ID linking.
// Replaces the retired bulk sync (Phase 8.1). Set-once: linked employees
// show a read-only Object ID; unlinked employees get an inline input.
// =============================================================================

interface EmployeeEntraLink {
  employeeId: string;
  employeeNumber: string;
  displayName: string;
  email: string;
  entraObjectId: string | null;
  isLinked: boolean;
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function EntraLinkTab() {
  const [items, setItems] = useState<EmployeeEntraLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(true);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setItems(await api.get('/api/admin/entra-link/employees')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function link(employeeId: string) {
    const value = (inputs[employeeId] || '').trim();
    if (!GUID_PATTERN.test(value)) return;
    try {
      setError(''); setSuccess(''); setSavingId(employeeId);
      const result = await api.post<EmployeeEntraLink>(`/api/admin/entra-link/employees/${employeeId}`, { entraObjectId: value });
      setSuccess(`${result.displayName} linked to Entra ID.`);
      setInputs(prev => { const next = { ...prev }; delete next[employeeId]; return next; });
      await load();
    } catch (e: any) {
      let message = e.message;
      try { const body = e.body ? JSON.parse(e.body) : null; message = body?.message || body?.error || message; } catch { /* keep message */ }
      setError(message);
    } finally {
      setSavingId(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  const unlinkedCount = items.filter(i => !i.isLinked).length;
  const visible = showUnlinkedOnly ? items.filter(i => !i.isLinked) : items;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: '#F0FDF4', color: '#15803D', fontSize: 13, fontWeight: 600 }}>
          ✓ {success}
        </div>
      )}

      <Card>
        <h4 className={styles.formTitle}>Entra ID Linking</h4>
        <p className={styles.muted} style={{ marginBottom: 12 }}>
          Links an employee record to their Microsoft Entra account so they can log in and see their own data.
          Paste the Object ID from Azure Portal → Entra ID → Users → select user → Object ID.
          Linking is set-once — a linked employee cannot be changed here.
        </p>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showUnlinkedOnly} onChange={e => setShowUnlinkedOnly(e.target.checked)} />
          Show unlinked only ({unlinkedCount})
        </label>
      </Card>

      <div style={{ height: 16 }} />

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr><th>Employee #</th><th>Name</th><th>Email</th><th>Entra Object ID</th><th></th></tr></thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={5} className={styles.muted} style={{ textAlign: 'center', padding: 20 }}>
                {showUnlinkedOnly ? 'All active employees are linked ✓' : 'No employees found'}
              </td></tr>
            )}
            {visible.map(item => {
              const value = inputs[item.employeeId] || '';
              const valid = GUID_PATTERN.test(value.trim());
              return (
                <tr key={item.employeeId}>
                  <td className={styles.mono}>{item.employeeNumber}</td>
                  <td className={styles.bold}>{item.displayName}</td>
                  <td className={styles.muted}>{item.email}</td>
                  <td className={styles.mono}>
                    {item.isLinked ? (
                      item.entraObjectId
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={e => setInputs(prev => ({ ...prev, [item.employeeId]: e.target.value }))}
                        placeholder="Paste Object ID, e.g. 3c39151a-4ad0-47c5-..."
                        style={{
                          width: '100%', minWidth: 280, padding: '6px 10px', fontSize: 13,
                          fontFamily: 'monospace', border: '1.5px solid ' + (value && !valid ? '#F04B41' : '#E2E7EC'),
                          borderRadius: 6
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {item.isLinked ? (
                      <span className={styles.active}>Linked</span>
                    ) : (
                      <button
                        className={styles.saveBtn}
                        onClick={() => link(item.employeeId)}
                        disabled={!valid || savingId === item.employeeId}
                        style={{ opacity: !valid || savingId === item.employeeId ? 0.5 : 1 }}
                      >
                        {savingId === item.employeeId ? 'Linking...' : 'Link'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Shared Form Field
// =============================================================================

function FormField({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        className={styles.fieldInput}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
